// EditarEvento.jsx
import {
  idsIguais,
  getUsuarioLogadoAdaptado as getUsuarioLogado,
} from "../../utils/ids";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { X, Plus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import "./EditarEvento.scss";
import { ReactComponent as ArrowLeftIcon } from "../../assets/seta.svg";
import ModalFuncionalidades from "../../components/funcionalidadesEvento/ModalFuncionalidades";
import {
  getTurmas,
  recarregarSeNecessario as recarregarTurmas,
} from "../../services/turmasStore";
import { formatDateToInput as formatDateInputValue } from "../../utils/formatting";

import {
  getEventos,
  upsertEvento,
  deleteEvento,
} from "../../services/eventosStore";
import { usePermissaoEventos } from "../../hooks/usePermissaoEventos";
import { useDisciplinaAtual } from "../../hooks/useDisciplinaAtual";

const MSG_SEM_PERMISSAO = "Este usuário não pode efetuar essa ação.";

const TITULO_MAX = 60;
const DESCRICAO_MAX = 800;

function capitalizeFirst(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function parseDataEvento(ev) {
  if (!ev) return null;

  const raw =
    ev.dataEvento ??
    ev.data ??
    ev.dataInicio ??
    ev.data_inicio ??
    ev.inicio ??
    ev.startDate ??
    null;

  if (!raw) return null;

  // ✅ CORREÇÃO PRINCIPAL (YYYY-MM-DD como LOCAL)
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [yyyy, mm, dd] = raw.split("-").map(Number);
    return new Date(yyyy, mm - 1, dd);
  }

  // fallback normal
  const isoTry = new Date(raw);
  if (!Number.isNaN(isoTry.getTime())) return isoTry;

  // formato BR
  if (typeof raw === "string" && raw.includes("/")) {
    const [dd, mm, yyyy] = raw.split("/").map(Number);
    if (dd && mm && yyyy) {
      const dt = new Date(yyyy, mm - 1, dd);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
  }

  return null;
}

function mapTurma(t) {
  // Label = "Nome - Complemento" (igual ao criar evento).
  const nome = String(t?.nome || "").trim();
  const comp = String(t?.complemento || "").trim();
  const label = nome
    ? comp
      ? `${nome} - ${comp}`
      : nome
    : `Turma ${t?.id ?? ""}`.trim();
  return {
    id: t.id,
    nome: label,
  };
}

const EditarEvento = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [startDate, setStartDate] = useState(null);

  const [notificacoes, setNotificacoes] = useState({
    calendario: true,
    destaque: false,
  });

  const [confirmacaoPresenca, setConfirmacaoPresenca] = useState(false);
  const [modalFuncAberto, setModalFuncAberto] = useState(false);

  const [modoTurmas, setModoTurmas] = useState("all");
  const [turmaSelecionadaId, setTurmaSelecionadaId] = useState("");
  const [turmasIds, setTurmasIds] = useState([]);
  const [turmasCache, setTurmasCache] = useState(() => getTurmas());

  const tituloCount = useMemo(() => titulo.length, [titulo]);
  const descricaoCount = useMemo(() => descricao.length, [descricao]);

  const usuario = useMemo(() => getUsuarioLogado(), []);
  const isCoordenador = (usuario?.tipo || "").toLowerCase() === "coordenador";

  // Permissão de editar eventos é por turma (turma de contexto, reativo).
  const turmaContexto = useDisciplinaAtual();
  const { pode: podeEditarEventos } = usePermissaoEventos(turmaContexto);
  const bloqueado = !podeEditarEventos;

  const avisarBloqueio = () => {
    Swal.fire({
      title: "Ação não permitida",
      text: MSG_SEM_PERMISSAO,
      icon: "info",
      confirmButtonText: "Entendi",
      confirmButtonColor: "#2E4A67",
    });
  };

  const instituicaoId = useMemo(
    () => usuario?.faculdadeId || usuario?.instituicaoId || null,
    [usuario],
  );

  // Coordenação selecionada no menu (filtra turmas igual ao criar evento).
  // Não precisa reagir a mudança: trocar coordenação sai da página (efeito abaixo).
  const coordenacaoAtualId = useMemo(
    () => localStorage.getItem("coordenacaoAtualId") || "",
    [],
  );

  useEffect(() => {
    if (!isCoordenador || !instituicaoId) return;
    recarregarTurmas(instituicaoId).catch(() => {});
    const onChanged = () => setTurmasCache(getTurmas(instituicaoId));
    window.addEventListener("turmas:changed", onChanged);
    return () => {
      window.removeEventListener("turmas:changed", onChanged);
    };
  }, [isCoordenador, instituicaoId]);

  // Trocar de contexto no menu sai da edição → volta para Eventos Publicados.
  // Coordenador troca COORDENAÇÃO; responsável/aluno-responsável troca TURMA.
  // O evento editado pode não pertencer ao novo contexto.
  useEffect(() => {
    const sair = () => navigate("/eventos");
    window.addEventListener("coordenacaoAtual:changed", sair);
    window.addEventListener("disciplinaAtual:changed", sair);
    return () => {
      window.removeEventListener("coordenacaoAtual:changed", sair);
      window.removeEventListener("disciplinaAtual:changed", sair);
    };
  }, [navigate]);

  const turmasDisponiveis = useMemo(() => {
    if (!isCoordenador) return [];
    return (
      turmasCache
        .filter(
          (t) => !instituicaoId || idsIguais(t?.instituicaoId, instituicaoId),
        )
        // Filtra pela coordenação selecionada (turma pode estar em N coordenações).
        .filter((t) => {
          if (!coordenacaoAtualId) return true;
          const ids = Array.isArray(t?.coordenacaoIds) ? t.coordenacaoIds : [];
          return ids.some((id) => String(id) === String(coordenacaoAtualId));
        })
        .map(mapTurma)
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
    );
  }, [isCoordenador, instituicaoId, turmasCache, coordenacaoAtualId]);

  const turmaIdToNome = useMemo(() => {
    const m = new Map();
    turmasDisponiveis.forEach((t) => m.set(String(t.id), t.nome));
    return m;
  }, [turmasDisponiveis]);

  const setAllTurmas = () => {
    const idsAll = turmasDisponiveis.map((t) => String(t.id)).filter(Boolean);
    setModoTurmas("all");
    setTurmaSelecionadaId("");
    setTurmasIds(idsAll);
  };

  const setSomeTurmas = () => {
    setModoTurmas("some");
    setTurmaSelecionadaId("");
  };

  const addTurma = () => {
    if (modoTurmas !== "some") return;
    if (!turmaSelecionadaId) return;

    const idStr = String(turmaSelecionadaId);
    setTurmasIds((prev) => (prev.includes(idStr) ? prev : [...prev, idStr]));
    setTurmaSelecionadaId("");
  };

  const removeTurma = (id) => {
    setTurmasIds((prev) => prev.filter((x) => String(x) !== String(id)));
  };

  const getEventoAtual = useCallback(() => {
    const todos = getEventos([]);
    return todos.find((e) => String(e.id) === String(id)) || null;
  }, [id]);

  useEffect(() => {
    const evento = getEventoAtual();

    if (!evento) {
      Swal.fire({
        title: "Evento não encontrado",
        text: "Não foi possível localizar este evento.",
        icon: "error",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#2E4A67",
      }).then(() => navigate("/eventos"));
      return;
    }

    if (
      usuario?.faculdadeId &&
      !idsIguais(evento.instituicaoId, usuario.faculdadeId)
    ) {
      Swal.fire({
        title: "Acesso negado",
        text: "Você não tem permissão para editar este evento.",
        icon: "error",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#2E4A67",
      }).then(() => navigate("/eventos"));
      return;
    }

    setTitulo(evento.titulo || "");
    setDescricao(evento.descricao || "");
    setStartDate(parseDataEvento(evento));

    setNotificacoes({
      calendario: !!evento.calendario,
      destaque: !!evento.destaque,
    });

    setConfirmacaoPresenca(!!evento.confirmacaoPresenca);

    if (isCoordenador) {
      const idsEvento = Array.from(
        new Set(
          (
            evento.turmasIds ||
            evento.disciplinaIds ||
            (evento.disciplinaId ? [evento.disciplinaId] : [])
          )
            .map(String)
            .filter(Boolean),
        ),
      );

      const idsAll = turmasDisponiveis.map((t) => String(t.id)).filter(Boolean);

      const cobreTudo =
        idsAll.length > 0 &&
        idsAll.every((idTurma) => idsEvento.includes(idTurma));

      if (!idsEvento.length || cobreTudo) {
        setModoTurmas("all");
        setTurmasIds(idsAll);
      } else {
        setModoTurmas("some");
        setTurmasIds(idsEvento);
      }

      setTurmaSelecionadaId("");
    }
  }, [getEventoAtual, navigate, usuario, isCoordenador, turmasDisponiveis]);

  const handleSalvar = async () => {
    if (bloqueado) {
      avisarBloqueio();
      return;
    }
    if (!titulo.trim()) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Por favor, preencha o título do evento.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (!descricao.trim()) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Por favor, preencha a descrição do evento.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (!notificacoes.calendario && !notificacoes.destaque) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Selecione o tipo de notificação do evento.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (notificacoes.calendario && !startDate) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Selecione a data do evento para aviso no calendário.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (isCoordenador && modoTurmas === "some" && turmasIds.length === 0) {
      Swal.fire({
        title: "Selecione ao menos uma turma",
        text: "Você está em 'Selecionar'. Adicione pelo menos uma turma ou troque para 'Todas'.",
        icon: "warning",
        confirmButtonText: "Ok",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    try {
      Swal.fire({
        title: "Salvando alterações...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const original = getEventoAtual();
      if (!original) throw new Error("Evento não encontrado");

      const dataEventoISO =
        notificacoes.calendario && startDate
          ? `${startDate.getFullYear()}-${String(
              startDate.getMonth() + 1,
            ).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`
          : null;

      const turmasIdsFinal = isCoordenador
        ? Array.from(
            new Set(
              (modoTurmas === "all"
                ? turmasDisponiveis.map((t) => String(t.id))
                : turmasIds.map(String)
              ).filter(Boolean),
            ),
          )
        : undefined;

      const atualizado = {
        ...original,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        calendario: !!notificacoes.calendario,
        destaque: !!notificacoes.destaque,
        confirmacaoPresenca: !!notificacoes.calendario && confirmacaoPresenca,
        dataEvento: dataEventoISO,
        ultimaAtualizacao: new Date().toISOString(),
        ...(isCoordenador
          ? { turmasIds: turmasIdsFinal, disciplinaIds: turmasIdsFinal }
          : {}),
      };

      Object.keys(atualizado).forEach((k) => {
        if (atualizado[k] === undefined) delete atualizado[k];
      });

      await upsertEvento(atualizado);

      Swal.close();

      await Swal.fire({
        title: "Sucesso!",
        text: "As alterações foram salvas.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        didOpen: () => Swal.hideLoading(),
      });

      navigate("/eventos");
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Erro ao salvar",
        text: "Não foi possível salvar as alterações. Tente novamente em instantes.",
        icon: "error",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleExcluir = async () => {
    if (bloqueado) {
      avisarBloqueio();
      return;
    }
    const confirm = await Swal.fire({
      title: "Excluir evento?",
      text: "Essa ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2E4A67",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      const original = getEventoAtual();
      if (!original) {
        await Swal.fire({
          title: "Evento não encontrado",
          text: "Esse evento já não está disponível.",
          icon: "info",
          confirmButtonColor: "#2E4A67",
        });
        navigate("/eventos");
        return;
      }

      // Salva o usuário ANTES de qualquer operação no store
      const usuarioAntes = localStorage.getItem("usuario");

      deleteEvento(original.id);

      // Restaura o usuário caso o store tenha tocado no localStorage
      if (usuarioAntes && !localStorage.getItem("usuario")) {
        localStorage.setItem("usuario", usuarioAntes);
      }

      await Swal.fire({
        title: "Excluído",
        text: "O evento foi excluído com sucesso.",
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#2E4A67",
      });

      navigate("/eventos");
    } catch {
      Swal.fire({
        title: "Erro ao excluir",
        text: "Não foi possível excluir o evento. Tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#d33",
      });
    }
  };

  return (
    <div
      className={`painel-evento ${isCoordenador ? "criar-evento-coordenador-page" : ""}`}
    >
      <div
        className={`eventos-wrap ${isCoordenador ? "eventos-wrap--coordenador" : ""}`}
      >
        <div className="editar-topbar">
          <button
            type="button"
            className="btn-voltar"
            onClick={() => navigate(-1)}
          >
            <span className="btn-voltar__seta" aria-hidden="true">
              <ArrowLeftIcon className="icon-arrow" />
            </span>
            <span>Voltar</span>
          </button>
        </div>

        {/* Bloqueio por falta de permissão: conteúdo esmaecido + overlay que
            intercepta cliques e exibe o aviso. "Voltar" fica livre acima. */}
        <div
          className={`editar-evento-conteudo ${
            bloqueado ? "editar-evento-conteudo--bloqueado" : ""
          }`}
        >
          {bloqueado && (
            <div
              className="permissao-overlay"
              onClick={avisarBloqueio}
              role="button"
              tabIndex={0}
              aria-label={MSG_SEM_PERMISSAO}
            />
          )}

          <section
            className={`card-bloco ${
              isCoordenador ? "criar-evento-card criar-evento-card--top" : ""
            }`}
          >
            <h1 className={isCoordenador ? "page-title" : "titulo-sessao"}>
              Editar evento
            </h1>

            <div
              className={
                isCoordenador
                  ? "formulario formulario--coordenador"
                  : "formulario"
              }
            >
              <div className={isCoordenador ? "field" : "campo"}>
                <label className="titulo-evento">Título</label>
                <input
                  type="text"
                  placeholder="Digite o título do evento"
                  className="input-estilizado"
                  value={titulo}
                  maxLength={TITULO_MAX}
                  onChange={(e) => {
                    const raw = e.target.value || "";
                    const limited = raw.slice(0, TITULO_MAX);
                    setTitulo(capitalizeFirst(limited));
                  }}
                  required
                />
                <div className="contador-campo">
                  {tituloCount}/{TITULO_MAX}
                </div>
              </div>

              <div className={isCoordenador ? "field" : "campo"}>
                <label>Descrição</label>
                <textarea
                  rows="10"
                  placeholder="Descreva os detalhes do evento"
                  className="input-estilizado"
                  value={descricao}
                  maxLength={DESCRICAO_MAX}
                  onChange={(e) => {
                    const raw = e.target.value || "";
                    setDescricao(raw.slice(0, DESCRICAO_MAX));
                  }}
                  required
                />
                <div className="contador-campo">
                  {descricaoCount}/{DESCRICAO_MAX}
                </div>
              </div>
            </div>
          </section>

          {isCoordenador && (
            <section className="card-bloco criar-evento-card">
              <h2 className="section-title">Turmas</h2>

              <div className="turmas-toggle">
                <button
                  type="button"
                  className={`turmas-toggle__btn ${modoTurmas === "all" ? "is-active" : ""}`}
                  onClick={setAllTurmas}
                >
                  Todas
                </button>

                <button
                  type="button"
                  className={`turmas-toggle__btn ${modoTurmas === "some" ? "is-active" : ""}`}
                  onClick={setSomeTurmas}
                >
                  Selecionar
                </button>
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <div className="turmas-select-row">
                  <div className="select-wrap">
                    <select
                      className="select-estilizado"
                      value={turmaSelecionadaId}
                      onChange={(e) => setTurmaSelecionadaId(e.target.value)}
                      disabled={modoTurmas === "all"}
                    >
                      <option value="">Selecionar turma</option>

                      {turmasDisponiveis
                        .filter((t) => !turmasIds.includes(String(t.id)))
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nome}
                          </option>
                        ))}
                    </select>

                    <span className="select-arrow" aria-hidden="true">
                      ▾
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn-add-turma"
                    onClick={addTurma}
                    disabled={modoTurmas === "all" || !turmaSelecionadaId}
                  >
                    Adicionar
                  </button>
                </div>

                {turmasIds.length > 0 && (
                  <div className="turmas-chips">
                    {turmasIds.map((tid) => (
                      <div className="chip-turma" key={tid}>
                        <span className="chip-text">
                          {turmaIdToNome.get(String(tid)) || `Turma ${tid}`}
                        </span>

                        {modoTurmas === "some" && (
                          <button
                            type="button"
                            className="chip-remove"
                            onClick={() => removeTurma(tid)}
                            aria-label="Remover turma"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {modoTurmas === "all" && (
                  <div className="turmas-info">
                    Todas as turmas serão selecionadas.
                  </div>
                )}
              </div>
            </section>
          )}

          <section
            className={`card-bloco ${isCoordenador ? "criar-evento-card" : ""}`}
          >
            {isCoordenador ? (
              <h2 className="section-title">Tipo de notificação</h2>
            ) : (
              <div className="campo">
                <label className="label-notificacao">Tipo de notificação</label>
              </div>
            )}

            <div className={isCoordenador ? "field" : "campo"}>
              <div className="opcoes-bolinha opcoes-bolinha--compacta">
                <div
                  className="item-bolinha"
                  onClick={() => {
                    setNotificacoes((prev) => {
                      const next = { ...prev, calendario: !prev.calendario };
                      if (!next.calendario) {
                        setStartDate(null);
                        // Funcionalidades só existem com calendário.
                        setConfirmacaoPresenca(false);
                      }
                      return next;
                    });
                  }}
                  role="checkbox"
                  aria-checked={notificacoes.calendario}
                  tabIndex={0}
                >
                  <div
                    className={`circular-check ${
                      notificacoes.calendario ? "active" : ""
                    }`}
                  />
                  <span>Aviso no calendário</span>
                </div>

                <div
                  className="item-bolinha"
                  onClick={() =>
                    setNotificacoes((prev) => ({
                      ...prev,
                      destaque: !prev.destaque,
                    }))
                  }
                  role="checkbox"
                  aria-checked={notificacoes.destaque}
                  tabIndex={0}
                >
                  <div
                    className={`circular-check ${
                      notificacoes.destaque ? "active" : ""
                    }`}
                  />
                  <span>Aviso em destaque</span>
                </div>
              </div>
            </div>

            {notificacoes.calendario && (
              <div className={isCoordenador ? "field" : "campo"}>
                <label>Data do evento</label>

                <div className="linha-data-funcionalidades">
                  <div className="input-calendario-wrapper">
                    <input
                      type="date"
                      className="input-estilizado"
                      value={formatDateInputValue(startDate)}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) =>
                        setStartDate(
                          e.target.value
                            ? parseDataEvento({ dataEvento: e.target.value })
                            : null,
                        )
                      }
                      onClick={(e) => e.target.showPicker?.()}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn-funcionalidades"
                    onClick={() => setModalFuncAberto(true)}
                    aria-label="Funcionalidades do evento"
                    title="Funcionalidades do evento"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            )}
            <div className="container-btn-acoes">
              <button
                type="button"
                className="btn-publicar-editar"
                onClick={handleSalvar}
              >
                Salvar alterações
              </button>

              <button
                type="button"
                className="btn-excluir-editar"
                onClick={handleExcluir}
              >
                Excluir evento
              </button>
            </div>

            <ModalFuncionalidades
              aberto={modalFuncAberto}
              valorInicial={confirmacaoPresenca}
              onSalvar={(valor) => {
                setConfirmacaoPresenca(valor);
                setModalFuncAberto(false);
              }}
              onFechar={() => setModalFuncAberto(false)}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default EditarEvento;
